import type { FC } from 'react'
import { useEffect, useState } from 'react'

import { LoaderCircle, Popup, Table } from '@skytech/ui'
import { Container } from '@skytech/ui/Container'
import { Td } from '@skytech/ui/Table/Td'
import { Th } from '@skytech/ui/Table/Th'
import { getQueryFilter, getSortedList, useDebounce } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { NoResults } from '@/components/NoResults'
import { useSearchParamsByKey, useSortParams } from '@/hooks/searchParams'
import { useRenderLimit } from '@/hooks/use-render-limit'
import useUser, { useProfile } from '@/hooks/user'
import { ExportToExcel } from '@/modules/admin/components/ExportToExcel'
import queries from '@/modules/admin/queries'
import adminResources from '@/modules/admin/resources'
import type { Asset, LeasedAsset } from '@/modules/admin/resources'
import { APP_ROUTES } from '@/util/app-routes'

import { ActionPopup } from './AssetLeased/ActionPopup'
import { AllAssetsCheckbox } from './AssetLeased/AllAssetsCheckbox'
import { SendSMSPopup } from './AssetLeased/SendSMSPopup'
import { AssetListItem } from './AssetListItem'
import { AssetLinkPopup } from './AssetListItem/AssetLinkPopup'
import { AssetsLeasedProvider, useLeasedAssets } from './AssetsLeased.context'
import { AssetStats } from './AssetStats'
import { AssetTop } from './AssetTop'
import {
  getFilterByKey,
  getIsWithinTimePeriod,
  isLeasedAssetsList,
  monthDiff,
  useLeasedFeatureAccess,
} from './helpers'

import './index.styles.scss'

const Assets: FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const translate = useTranslation()
  const { locale } = useAppContext()

  const profile = useProfile()
  const user = useUser()

  if (!profile) throw new Error('No profile found for Assets!')
  if (!user) throw new Error('No user found for Assets!')

  const [queryAssetOwnership] = useSearchParamsByKey('assetOwnership')
  const isLeasedOwnershipType = queryAssetOwnership === 'LEASED'
  const showLeasedFeatures = isLeasedOwnershipType && useLeasedFeatureAccess(user.role)

  const { data: allData, isLoading: isAllDataLoading } = useQuery<Asset[]>(
    queries.adminAssetList(),
    adminResources.getAssetList,
    {
      enabled: queryAssetOwnership !== 'LEASED',
    }
  )
  const { data: leasedData, isLoading: isLeasedDataLoading } = useQuery(
    queries.adminLeasedAssetList(),
    adminResources.getLeasedAssetList,
    {
      enabled: isLeasedOwnershipType,
    }
  )
  const data: (Asset | LeasedAsset)[] | undefined = isLeasedOwnershipType
    ? leasedData?.assets
    : allData
  const isLoading = isLeasedOwnershipType ? isLeasedDataLoading : isAllDataLoading

  const [querySearch, setSearchQuery] = useSearchParamsByKey('search')
  const [queryAssetType, setQueryAssetType] = useSearchParamsByKey('assetType')
  const [queryAssetAge, setQueryAssetAge] = useSearchParamsByKey('assetAge')
  const [queryUserType] = useSearchParamsByKey('userType')
  const [queryAssetStatus] = useSearchParamsByKey('assetStatus')
  const [queryEntireConcern] = useSearchParamsByKey('entireConcern')
  const [queryAllAssets] = useSearchParamsByKey('allAssets')
  const [queryLeaseReturn] = useSearchParamsByKey('leaseReturn')
  const [{ querySort, querySortBy }, { handleSort }] = useSortParams<keyof (Asset | LeasedAsset)>()

  const [searchFilter, setSearchFilter] = useState(querySearch || '')
  const [isStatsPopupOpen, setIsStatsPopupOpen] = useState(false)
  const [isAssetLinkPopupOpen, setIsAssetLinkPopupOpen] = useState(false)

  const [assetLink, setAssetLink] = useState('/')
  const debouncedAssetSearchTerm = useDebounce(searchFilter, 500)

  const { action, selectedList, toggleSelect, selectAll } = useLeasedAssets()

  const stringMatcher = (item: Asset | LeasedAsset) => [
    item.assetDescription,
    item.costObjectId,
    item.costObjectName,
    item.customerName,
    item.imei,
    item.snr,
    translate(`asset_type/${item.assetType}`),
    translate(`asset_user_type/${item.userType}`),
    translate(`asset_status/${item.status}`),
    item.address,
    item.addressZip,
    item.addressCity,
    item.costObjectCode,
    queryAssetOwnership === 'LEASED' && item.leaseStart,
    queryAssetOwnership === 'LEASED' && item.leaseEnd,
  ]

  useEffect(() => {
    setSearchFilter(querySearch || '')
  }, [querySearch])

  useEffect(() => {
    setSearchQuery(debouncedAssetSearchTerm)
  }, [debouncedAssetSearchTerm, setSearchQuery])

  const goToDashboard = () => navigate(APP_ROUTES.HOME)

  const filterByKey = getFilterByKey({ queryAssetAge, queryAssetType, search: location.search })

  const { itemsFiltered, itemsFilteredForStats } = (data || []).reduce<{
    itemsFiltered: (Asset | LeasedAsset)[]
    itemsFilteredForStats: (Asset | LeasedAsset)[]
  }>(
    (acc, el) => {
      const diff = monthDiff(new Date(el.received || ''), new Date())
      const isPeriodConditionMet = Boolean(
        (queryAssetAge === 'Missing date' && !el.received) ||
          (queryAssetAge === '48+ months' && diff > 48) ||
          (queryAssetAge === '36-48 months' && diff <= 48 && diff > 36) ||
          (queryAssetAge === '24-36 months' && diff <= 36 && diff > 24) ||
          (queryAssetAge === '12-24 months' && diff <= 24 && diff > 12) ||
          (queryAssetAge === '6-12 months' && diff <= 12 && diff > 6) ||
          (queryAssetAge === '3-6 months' && diff <= 6 && diff > 3) ||
          (queryAssetAge === '0-3 months' && el.received && diff <= 3)
      )
      const ownershipFilter = queryAssetOwnership
        ? queryAssetOwnership === 'OWNED'
          ? !el.isLeased
          : el.isLeased
        : true
      const leaseReturnFilter = queryLeaseReturn
        ? el.isLeased &&
          el.leaseEnd &&
          getIsWithinTimePeriod(Number(queryLeaseReturn), new Date(el.leaseEnd))
        : true
      const typeFilter = queryAssetType ? el.assetType === queryAssetType : true
      const ageFilter = !queryAssetAge ? true : isPeriodConditionMet
      const statsTypeFilter =
        queryAssetType && filterByKey === 'assetType' ? el.assetType === queryAssetType : true
      const statsAgeFilter =
        !queryAssetAge || filterByKey !== 'assetAge' ? true : isPeriodConditionMet

      const queryFilter = getQueryFilter(searchFilter, stringMatcher)(el)
      const assetTypeFilter =
        queryAssetStatus && queryAssetStatus !== 'all' ? el.status === queryAssetStatus : true
      const userTypeFilter =
        queryUserType && queryUserType !== 'all' ? el.userType === queryUserType : true
      const entireConcernFilter =
        queryEntireConcern && queryEntireConcern === 'true'
          ? true
          : el.customerId === user?.customerId
      const commonFilters =
        queryFilter &&
        assetTypeFilter &&
        userTypeFilter &&
        entireConcernFilter &&
        ownershipFilter &&
        leaseReturnFilter

      const isActiveFilter = queryAllAssets === 'true' ? true : el.active ? true : false

      if (commonFilters && typeFilter && ageFilter && isActiveFilter) acc.itemsFiltered.push(el)
      if (commonFilters && statsTypeFilter && statsAgeFilter && isActiveFilter)
        acc.itemsFilteredForStats.push(el)
      return acc
    },
    { itemsFilteredForStats: [], itemsFiltered: [] }
  )
  const sortedList = getSortedList({ list: itemsFiltered, querySort, querySortBy, locale })
  const excelExportData = itemsFiltered.map(
    ({
      id,
      assetDescription,
      imei,
      snr,
      received,
      leaseStart,
      leaseEnd,
      cost,
      // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
      customerId,
      // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
      costObjectId,
      // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
      imageUrl,
      costObjectName,
      ...item
    }) => ({
      id,
      assetDescription,
      imei,
      snr,
      received,
      leaseStart,
      leaseEnd,
      cost,
      currency: profile?.currency,
      ...item,
      userDescription: costObjectName,
    })
  )

  const { visibleList: visibleListAssets, ListLoaderComponent } = useRenderLimit(
    sortedList || [],
    30
  )

  if (isLoading)
    return (
      <Container onSwipedLeft={goToDashboard} className="assets">
        <Container.Header>
          <Translation id="Assets" />
        </Container.Header>
        <LoaderCircle />
      </Container>
    )

  const isAllAssetsFromUserConcern = !data?.some(asset => asset.customerId !== user?.customerId)
  const isAnyAssetsLeased = data?.some(asset => asset.isLeased) ?? false

  const isSendSMSAvailable =
    sortedList.filter(asset => selectedList.includes(asset.id) && asset.costObjectCode).length !== 0
  const showLeasedPopups = showLeasedFeatures && Boolean(action)

  return (
    <Container onSwipedLeft={goToDashboard} className="assets">
      <Container.Header>
        <Translation id="Assets" />
      </Container.Header>
      <Popup
        variant="wide"
        ariaLabel={translate('Assets stats')}
        isOpen={isStatsPopupOpen}
        onClose={() => setIsStatsPopupOpen(false)}
      >
        <AssetStats
          filteredAssetList={itemsFilteredForStats}
          queryAssetType={queryAssetType}
          queryAssetAge={queryAssetAge}
          onSetAssetTypeFilter={setQueryAssetType}
          onSetAssetAgeFilter={setQueryAssetAge}
        />
      </Popup>
      {showLeasedFeatures && (
        <AssetLinkPopup
          isPopupOpen={isAssetLinkPopupOpen}
          onClose={setIsAssetLinkPopupOpen}
          link={assetLink}
        />
      )}
      {showLeasedPopups &&
        (action === 'SendSMS' ? (
          <SendSMSPopup
            assets={sortedList.filter(
              asset => selectedList.includes(asset.id) && asset.costObjectCode
            )}
          />
        ) : (
          isLeasedAssetsList(sortedList) && (
            <ActionPopup
              assets={sortedList.filter(asset => selectedList.includes(asset.id)) as LeasedAsset[]}
            />
          )
        ))}

      <AssetTop
        isAnyAssetsLeased={isAnyAssetsLeased}
        searchFilter={searchFilter}
        isLoading={isLoading}
        isAllAssetsFromUserConcern={isAllAssetsFromUserConcern}
        isSendSMSAvailable={isSendSMSAvailable}
        setSearchFilter={setSearchFilter}
        setIsStatsPopupOpen={setIsStatsPopupOpen}
      />

      <ExportToExcel
        data={excelExportData}
        filename={translate('Assets')}
        options={{
          columnFormats: {
            received: 'date',
            cost: 'amount',
            leaseStart: 'date',
            leaseEnd: 'date',
            downPaymentSum: 'amount',
          },
        }}
      />
      {itemsFiltered.length > 0 ? (
        <div className="table-container">
          <Table rowHeight="tall">
            <thead>
              <tr>
                {showLeasedFeatures && isLeasedAssetsList(sortedList) && (
                  <AllAssetsCheckbox
                    className="center p-1 pr-0 md:table-cell md:p-2 md:pr-0 lg:p-2.5 lg:pr-0 xl:p-4"
                    isSelected={selectedList.length !== 0}
                    isAllSelected={
                      selectedList.length === sortedList.length && selectedList.length !== 0
                    }
                    onClick={() => {
                      selectAll(sortedList as LeasedAsset[])
                    }}
                  />
                )}
                <Th className="hidden md:p-2 lg:table-cell lg:p-2.5 xl:p-4" />
                <Th
                  id="assetDescription"
                  className="w-auto md:w-1/4 md:p-2 lg:p-2.5 xl:p-4"
                  onClick={handleSort}
                >
                  <Translation id="name" />
                </Th>

                <Th
                  className="hidden md:table-cell md:p-2 lg:p-2.5 xl:p-4"
                  id="costObjectName"
                  onClick={handleSort}
                >
                  <Translation id="employee" />
                </Th>

                <Th
                  id="status"
                  className="hidden md:table-cell md:p-2 lg:p-2.5 xl:p-4"
                  onClick={handleSort}
                >
                  <Translation id="status" />
                </Th>

                <Th
                  className="hidden md:p-2 lg:table-cell lg:p-2.5 xl:p-4"
                  id="address"
                  onClick={handleSort}
                >
                  <Translation id="address" />
                </Th>

                {queryAssetOwnership !== 'LEASED' && (
                  <Th
                    className="hidden md:table-cell md:p-2 lg:p-2.5 xl:p-4"
                    id="received"
                    onClick={handleSort}
                  >
                    <Translation id="received" />
                  </Th>
                )}
                {isLeasedOwnershipType && (
                  <Th
                    className="hidden sm:table-cell md:p-2 lg:p-2.5 xl:p-4"
                    id="leaseStart"
                    onClick={handleSort}
                  >
                    <Translation id="Lease start" />
                  </Th>
                )}
                {isLeasedOwnershipType && (
                  <Th
                    className="hidden sm:table-cell md:p-2 lg:p-2.5 xl:p-4"
                    id="leaseEnd"
                    onClick={handleSort}
                  >
                    <Translation id="Lease end" />
                  </Th>
                )}

                <Th
                  className="hidden md:table-cell md:p-2 lg:p-2.5 xl:p-4"
                  id="cost"
                  onClick={handleSort}
                >
                  <Translation id="cost" />
                </Th>
              </tr>
            </thead>

            {isLoading ? (
              <tbody>
                <tr>
                  <Td>
                    <LoaderCircle />
                  </Td>
                </tr>
              </tbody>
            ) : (
              <tbody>
                {visibleListAssets.map(item => (
                  <AssetListItem
                    key={item.id}
                    searchFilter={searchFilter}
                    isLeasedOwnershipType={isLeasedOwnershipType}
                    isSelected={selectedList.includes(item.id)}
                    onSetAssetLinkPopupOpen={setIsAssetLinkPopupOpen}
                    onSetAssetLink={setAssetLink}
                    onSelectLeasedAsset={id => toggleSelect(id)}
                    showLeasedFeatures={showLeasedFeatures}
                    {...item}
                  />
                ))}
              </tbody>
            )}
          </Table>
          <ListLoaderComponent />
        </div>
      ) : (
        <NoResults />
      )}
    </Container>
  )
}

export default () => (
  <AssetsLeasedProvider>
    <Assets />
  </AssetsLeasedProvider>
)
